import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import LaunchWorkflowRunGeneration from '@Application/WorkflowRuns/WorkflowRun/Commands/LaunchWorkflowRunGeneration';
import launchWorkflowRunGeneration from '@Application/WorkflowRuns/WorkflowRun/launchWorkflowRunGeneration';
import { theme } from '@Guidelines/Theme/theme';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import describeElementsSource from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GenerateSection/StageCard/GeneratedDocumentRow/Helpers/describeElementsSource';
import useElementDescriber from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Hooks/useElementDescriber';
import generateConfirmSignal, {
	closeGenerateConfirm,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GenerateSection/Signals/generateConfirmSignal';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import type WorkflowRunView from '@UI/Store/WorkflowRuns/Views/WorkflowRunView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch } from '@verclam/synapse';
import { ConfirmDialog } from '@verclam/ui-kit';

const bodyStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	font-family: ${theme.fonts.body};
	font-size: 0.875rem;
	line-height: 1.6;
	color: ${theme.colors.inkSecondary};
`;

const eyebrowStyle = css`
	${theme.typography.eyebrow}
	margin: 0;
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

const introStyle = css`
	margin: 0;
`;

const listStyle = css`
	display: flex;
	flex-direction: column;
	margin: 0;
	padding: 0.25rem 1rem;
	list-style: none;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};

	& > li {
		display: flex;
		flex-direction: column;
		gap: 0.125rem;
		padding: 0.75rem 0;
		border-bottom: 1px solid ${theme.colors.borderStrong};
	}

	& > li:last-child {
		border-bottom: none;
	}
`;

const itemTitleStyle = css`
	font-size: 0.875rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1.35;
	color: ${theme.colors.ink};
`;

const itemDetailStyle = css`
	font-size: 0.78125rem;
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

const noteStyle = css`
	margin: 0;
	font-size: 0.78125rem;
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly preparation: WorkflowRunView;
};

/**
 * Confirmation before the single « Générer »: lists the elements the documents will be built from and the
 * order of the stages, and warns that the preparation stops receiving elements once launched.
 */
const GenerateConfirmDialog: React.FC<Props> = ({ subject, preparation }) => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const isVisible = useSignalValue(generateConfirmSignal);
	const [isLaunching, setIsLaunching] = React.useState(false);
	const { describeElement, labelOfDocumentType } = useElementDescriber(subject);

	const handleHide = React.useCallback(() => {
		closeGenerateConfirm();
	}, []);

	const handleConfirm = React.useCallback(() => {
		setIsLaunching(true);
		dispatch(
			launchWorkflowRunGeneration(new LaunchWorkflowRunGeneration(facilityId, subject, preparation.id)),
		)
			.catch(ignoreHandledError)
			.finally(() => {
				setIsLaunching(false);
				closeGenerateConfirm();
			});
	}, [dispatch, facilityId, subject, preparation.id]);

	const elementCount = preparation.sources.length;
	const message = (
		<div css={bodyStyle}>
			<p css={eyebrowStyle}>Avant de générer</p>
			<p css={introStyle}>
				Tous les éléments de la préparation seront utilisés, et les étapes s’enchaîneront
				d’elles-mêmes.
			</p>
			<ul css={listStyle}>
				{preparation.sources.map((element) => {
					const description = describeElement(element);

					return (
						<li key={element.id}>
							<span css={itemTitleStyle}>{description.title}</span>
							<span css={itemDetailStyle}>{description.subtitle}</span>
						</li>
					);
				})}
				<ConditionalLoader data={elementCount === 0}>
					<li css={itemDetailStyle}>Aucun élément dans la préparation.</li>
				</ConditionalLoader>
			</ul>
			<p css={eyebrowStyle}>Ordre des étapes</p>
			<ol css={listStyle}>
				{preparation.documentStages.map((stage) => (
					<li key={stage.stage}>
						<span css={itemTitleStyle}>Étape {stage.stage + 1}</span>
						<span css={itemDetailStyle}>
							{stage.documents
								.map((document) => labelOfDocumentType(document.documentTypeId))
								.join(', ')}
						</span>
					</li>
				))}
			</ol>
			<p css={noteStyle}>
				Après le lancement, la préparation ne reçoit plus d’éléments. Vous ne voulez pas d’un élément
				? Annulez et retirez-le de la préparation.
			</p>
		</div>
	);

	return (
		<ConfirmDialog
			blockScroll
			closable
			draggable={false}
			visible={isVisible}
			onHide={handleHide}
			header="Générer les documents ?"
			message={message}
			acceptLabel={`Générer à partir ${describeElementsSource(elementCount)}`}
			rejectLabel="Annuler"
			loading={isLaunching}
			accept={handleConfirm}
		/>
	);
};

export default GenerateConfirmDialog;
